import { useEffect, useState } from 'react';
import { get, post, session, fmt, parseBhd, ApiError } from '../lib/api';
import { Modal } from '../screens/Till';

interface Totals { gross: number; payableCash: number; rounding: number; vat: number }

interface ProviderCaps {
  kind: string; integrated: boolean; canVoid: boolean; canRefund: boolean;
  refundableSchemes: string[]; requiresManualApprovalCode: boolean; label: string;
}

interface Tender {
  method: string; amountMinor: number; tenderedMinor?: number;
  txnRef?: string; rrn?: string; authCode?: string; cardScheme?: string; cardLast4?: string;
}

const QUICK_CASH = [1000, 5000, 10000, 20000, 50000];

/**
 * Tender screen. Supports split payment across cash / card / store credit.
 *
 * The card section ADAPTS to whichever rail the terminal is configured for
 * (POS_MASTER_PLAN.md §3.4):
 *   mpgs_online     -> charge via the CrediMax online gateway (or simulator)
 *   manual_terminal -> cashier keys the amount into the CrediMax terminal and
 *                      types the approval code back in
 *   credimax_ecr    -> integrated terminal (pending CrediMax enablement)
 * The cashier never has to know which; the UI simply changes.
 */
export default function TenderModal({ totals, onClose, onConfirm }: {
  totals: Totals;
  onClose: () => void;
  onConfirm: (payments: Tender[]) => Promise<void>;
}) {
  const s = session.get()!;
  const [caps, setCaps] = useState<ProviderCaps | null>(null);
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // card entry state
  const [cardBusy, setCardBusy] = useState(false);
  const [pendingRef, setPendingRef] = useState<string | null>(null); // hosted-checkout txn awaiting confirm
  const [approval, setApproval] = useState('');
  const [scheme, setScheme] = useState('VISA');
  const [last4, setLast4] = useState('');
  const [reEnter, setReEnter] = useState('');

  const [cashInput, setCashInput] = useState('');

  useEffect(() => {
    get<any>(`/api/v1/payments/terminal-config?storeCode=${s.storeCode}&terminalCode=${s.terminalCode}`)
      .then((d) => setCaps(d.provider))
      .catch(() => setError('Could not read the terminal payment configuration'));
  }, []);

  const taken = tenders.reduce((n, t) => n + t.amountMinor, 0);
  // Cash rounding only applies once cash is actually involved.
  const hasCash = tenders.some((t) => t.method === 'cash');
  const target = hasCash ? totals.payableCash : totals.gross;
  const remaining = target - taken;

  function addTender(t: Tender) {
    setTenders((p) => [...p, t]);
    setError('');
  }

  function payCash(amountMinor: number, tenderedMinor: number) {
    const amt = Math.min(amountMinor, Math.max(0, totals.payableCash - taken));
    if (amt <= 0) return;
    addTender({ method: 'cash', amountMinor: amt, tenderedMinor });
    setCashInput('');
  }

  /** Online gateway (or simulator): the server performs the authorization. */
  async function payCardOnline() {
    const amount = remaining;
    if (amount <= 0) return;
    setCardBusy(true); setError('');
    try {
      const res = await post<any>('/api/v1/payments/authorize', {
        storeCode: s.storeCode, terminalCode: s.terminalCode,
        amountMinor: amount, reference: `POS-${Date.now()}`,
        description: `FEMI9 ${s.storeCode} till ${s.terminalCode}`,
      });
      const r = res.result;
      if (r.status === 'approved') {
        addTender({
          method: 'card_online', amountMinor: amount,
          txnRef: r.txnRef, rrn: r.rrn, authCode: r.authCode,
          cardScheme: r.cardScheme, cardLast4: r.cardLast4,
        });
      } else if (r.status === 'pending' && r.redirectUrl) {
        // Real Hosted Checkout: the customer completes payment on CrediMax's page.
        window.open(r.redirectUrl, 'credimax', 'width=480,height=720');
        setPendingRef(r.txnRef);
        setError('Complete the payment in the CrediMax window, then press "Confirm card payment".');
      } else {
        setError(r.message ?? `Card ${r.status}`);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Card authorization failed');
    } finally {
      setCardBusy(false);
    }
  }

  /** After hosted checkout: poll the gateway until the payment settles (up to ~45s). */
  async function verifyCardOnline() {
    if (!pendingRef) return;
    setCardBusy(true); setError('Checking payment status with CrediMax…');
    try {
      for (let attempt = 0; attempt < 15; attempt++) {
        const r = await post<any>('/api/v1/payments/verify', { provider: 'mpgs_online', txnRef: pendingRef });
        if (r.status === 'approved') {
          addTender({
            method: 'card_online', amountMinor: remaining,
            txnRef: r.txnRef, rrn: r.rrn, authCode: r.authCode,
            cardScheme: r.cardScheme, cardLast4: r.cardLast4,
          });
          setPendingRef(null);
          setError('');
          return;
        }
        if (r.status !== 'pending') {
          setError(r.message ?? `Card ${r.status}`);
          setPendingRef(null);
          return;
        }
        await new Promise((ok) => setTimeout(ok, 3000));
      }
      setError('Still processing after 45s — the payment may have completed. Check Reports, or press Confirm again.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not verify the payment');
    } finally {
      setCardBusy(false);
    }
  }

  /** Manual terminal: require the approval code AND a re-typed amount. */
  function payCardManual() {
    const amount = remaining;
    if (amount <= 0) return;
    if (!approval.trim()) { setError('Enter the approval code printed on the terminal slip'); return; }
    let reTyped: number;
    try { reTyped = parseBhd(reEnter); } catch { setError('Re-enter the amount as shown on the terminal'); return; }
    // Guards against the classic tip-and-type error: charging the wrong amount.
    if (reTyped !== amount) {
      setError(`You typed ${fmt(reTyped)} but the terminal should have charged ${fmt(amount)}. Check the slip.`);
      return;
    }
    addTender({
      method: 'card_terminal', amountMinor: amount,
      authCode: approval.trim(), rrn: approval.trim(),
      cardScheme: scheme, cardLast4: last4 || undefined,
    });
    setApproval(''); setReEnter(''); setLast4('');
  }

  async function confirm() {
    if (remaining !== 0) {
      setError(remaining > 0 ? `${fmt(remaining)} still to pay` : `Over-tendered by ${fmt(-remaining)}`);
      return;
    }
    setBusy(true);
    try { await onConfirm(tenders); }
    catch (err) { setError(err instanceof ApiError ? err.message : 'Could not complete the sale'); }
    finally { setBusy(false); }
  }

  const cashTender = tenders.find((t) => t.method === 'cash');
  const change = cashTender ? Math.max(0, (cashTender.tenderedMinor ?? 0) - cashTender.amountMinor) : 0;

  return (
    <Modal title="Payment" onClose={onClose} width="max-w-lg">
      <div className="space-y-4">
        <div className="flex justify-between items-baseline">
          <span className="text-gray-400">Total due</span>
          <span className="money text-3xl font-bold">{fmt(target)}</span>
        </div>
        {totals.rounding !== 0 && hasCash && (
          <div className="text-xs text-gray-500 -mt-3">
            includes {totals.rounding > 0 ? '+' : ''}{totals.rounding} fils cash rounding to the nearest 5
          </div>
        )}

        {tenders.length > 0 && (
          <div className="card divide-y divide-gray-800">
            {tenders.map((t, i) => (
              <div key={i} className="px-3 py-2 flex justify-between items-center text-sm">
                <div>
                  <span className="capitalize">{t.method.replace('_', ' ')}</span>
                  {t.cardScheme && <span className="text-gray-500 text-xs ml-2">{t.cardScheme}{t.cardLast4 ? ` ••${t.cardLast4}` : ''}</span>}
                  {t.authCode && <span className="text-gray-600 text-[11px] ml-2">auth {t.authCode}</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="money">{fmt(t.amountMinor)}</span>
                  <button className="text-gray-500 hover:text-red-400"
                    onClick={() => setTenders((p) => p.filter((_, j) => j !== i))}>×</button>
                </div>
              </div>
            ))}
            <div className="px-3 py-2 flex justify-between text-sm font-medium">
              <span className={remaining === 0 ? 'text-emerald-400' : 'text-amber-300'}>
                {remaining === 0 ? 'Fully paid' : remaining > 0 ? 'Remaining' : 'Over-tendered'}
              </span>
              <span className="money">{fmt(Math.abs(remaining))}</span>
            </div>
          </div>
        )}

        {change > 0 && (
          <div className="bg-emerald-950/50 border border-emerald-800 rounded-lg px-4 py-3 flex justify-between items-baseline">
            <span className="text-emerald-300 text-sm">Change due</span>
            <span className="money text-2xl font-bold text-emerald-300">{fmt(change)}</span>
          </div>
        )}

        {remaining > 0 && (
          <>
            {/* ── Cash ── */}
            <div>
              <div className="label">Cash</div>
              <div className="flex flex-wrap gap-2 mb-2">
                <button className="btn-ghost text-sm py-1.5"
                  onClick={() => payCash(remaining, remaining)}>Exact {fmt(remaining)}</button>
                {QUICK_CASH.filter((q) => q >= remaining).slice(0, 3).map((q) => (
                  <button key={q} className="btn-ghost text-sm py-1.5" onClick={() => payCash(remaining, q)}>
                    {fmt(q)}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <input className="input flex-1" placeholder="Amount handed over, e.g. 20.000"
                  value={cashInput} onChange={(e) => setCashInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    try { const v = parseBhd(cashInput); payCash(Math.min(v, remaining), v); }
                    catch { setError('Enter a valid amount'); }
                  }} />
                <button className="btn-ghost" onClick={() => {
                  try { const v = parseBhd(cashInput); payCash(Math.min(v, remaining), v); }
                  catch { setError('Enter a valid amount'); }
                }}>Add</button>
              </div>
            </div>

            {/* ── Card: adapts to the configured rail ── */}
            <div>
              <div className="label flex items-center justify-between">
                <span>Card</span>
                {caps && (
                  <span className="text-[11px] normal-case tracking-normal text-gray-500">{caps.label}</span>
                )}
              </div>

              {!caps && <div className="text-xs text-gray-500">Reading terminal configuration…</div>}

              {caps?.kind === 'mpgs_online' && (
                pendingRef ? (
                  <button className="btn-primary w-full" disabled={cardBusy} onClick={() => void verifyCardOnline()}>
                    {cardBusy ? 'Checking…' : `Confirm card payment ${fmt(remaining)}`}
                  </button>
                ) : (
                  <button className="btn-primary w-full" disabled={cardBusy} onClick={() => void payCardOnline()}>
                    {cardBusy ? 'Authorizing…' : `Charge card ${fmt(remaining)}`}
                  </button>
                )
              )}

              {caps?.kind === 'manual_terminal' && (
                <div className="space-y-2">
                  <div className="text-xs text-amber-300 bg-amber-950/40 border border-amber-900 rounded px-3 py-2">
                    Key <span className="money font-semibold">{fmt(remaining)}</span> into the CrediMax
                    terminal, then enter the approval code from the slip.
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input className="input" placeholder="Approval code" value={approval}
                      onChange={(e) => setApproval(e.target.value)} />
                    <input className="input" placeholder="Re-type amount" value={reEnter}
                      onChange={(e) => setReEnter(e.target.value)} />
                    <select className="input" value={scheme} onChange={(e) => setScheme(e.target.value)}>
                      {['VISA', 'MASTERCARD', 'AMEX', 'BENEFIT'].map((x) => <option key={x}>{x}</option>)}
                    </select>
                    <input className="input" placeholder="Last 4 (optional)" maxLength={4}
                      value={last4} onChange={(e) => setLast4(e.target.value)} />
                  </div>
                  {scheme === 'BENEFIT' && (
                    <div className="text-[11px] text-amber-400">
                      Note: CrediMax terminals may not permit refunds on BENEFIT debit.
                      Returns on this tender may have to go to store credit or cash.
                    </div>
                  )}
                  <button className="btn-primary w-full" onClick={payCardManual}>
                    Confirm card payment {fmt(remaining)}
                  </button>
                </div>
              )}

              {caps?.kind === 'credimax_ecr' && (
                <div className="text-xs text-red-300 bg-red-950/40 border border-red-900 rounded px-3 py-2">
                  This till is set to the integrated CrediMax terminal, which is not yet
                  enabled. Switch it to the online gateway or manual entry in
                  Back office → Terminals.
                </div>
              )}
            </div>

            {/* ── Other tenders ── */}
            <div className="grid grid-cols-2 gap-2">
              <button className="btn-ghost text-sm"
                onClick={() => addTender({ method: 'store_credit', amountMinor: remaining })}>
                Store credit {fmt(remaining)}
              </button>
              <button className="btn-ghost text-sm"
                onClick={() => addTender({ method: 'gift_card', amountMinor: remaining })}>
                Gift card {fmt(remaining)}
              </button>
            </div>
          </>
        )}

        {error && (
          <div className="text-sm text-red-300 bg-red-950/50 border border-red-900 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <button className="btn-ghost flex-1" onClick={onClose}>Cancel</button>
          <button className="btn-primary flex-1 h-11" disabled={busy || remaining !== 0} onClick={() => void confirm()}>
            {busy ? 'Completing…' : 'Complete sale'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
